import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOverdueBalance } from 'api/SupraController';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';
import { Statistic } from 'antd';
import { Card } from '@brokerbay/design-components';
import { getBalanceAndDate } from 'api/BillingController';
import { formatCurrency, featureEnabled } from 'utils';
import { Button, Color, Text } from '@bb/elements-web';
import { AuthContext } from 'contexts';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { SHOW_UPDATE_CARD_EVENT } from '../../../components/ManageCard/Content';
import { BillingInvoicesPageContext } from '../BillingInvoicesPageContext';

type BalanceCardProps =
  | BalanceCardForLegacyBillingUser
  | BalanceCardForOrg
  | BalanceCardForKeyholderBillingUser;

type BalanceCardForLegacyBillingUser = {
  orgId: string;
  userId: string;
  type: 'legacyUser';
};

type BalanceCardForOrg = {
  orgId: string;
  type: 'org';
};

type BalanceCardForKeyholderBillingUser = {
  billingCustomerNumber: string;
  type: 'keyholderBillingUser';
};

export const SUBSCRIPTION_UPDATED_EVENT = `onBillingPageSubscriptionUpdate_${Math.random()}`;

/**
 * Generate the content we'll display. We will display the outstanding balance
 * and the next invoice date, either for a keyholder billing user, for a
 * "legacy" user not utilizing keyholder billing, or for their organization.
 * Any time one of the props changes, everything is re-rendered.
 * @param props either BalanceCardForLegacyBillingUser or BalanceCardForOrg
 * @returns the JSX content for React
 */
const BalanceCard = (props: BalanceCardProps) => {
  const [balance, setBalance] = useState<number | null>(null);
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] =
    useState<Date | null>(null);
  const [customerNumber, setCustomerNumber] = useState('--');
  const { auth } = useContext(AuthContext);

  // Add context for overdue fees
  const billingContext = useContext(BillingInvoicesPageContext);
  const { proratedPricing, reinstatementFee, loadingOverdueFees } =
    billingContext || {};

  const isBalanceOverdue = useMemo(
    () =>
      featureEnabled('keyholderBillingFeature') &&
      auth.billingCustomerNumber &&
      (auth.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.suspended ||
        auth.supraSubscription?.status ===
          SupraSystemSubscriptionStatus.deactivated),
    [auth],
  );

  // Check if this is a balance for a user or for an org
  const isBalanceForLegacyBillingUser = useCallback(
    (input: unknown): input is BalanceCardForLegacyBillingUser => {
      const { type } = props;
      return type === 'legacyUser';
    },
    [props],
  );

  const isBalanceForOrg = useCallback(
    (input: unknown): input is BalanceCardForOrg => {
      const { type } = props;
      return type === 'org';
    },
    [props],
  );

  // Go fetch the data from the db
  const fetchData = useCallback(async () => {
    // Call the API to grab the balance for a user not utilizing keyholder billing
    if (isBalanceForLegacyBillingUser(props)) {
      const { orgId, userId } = props;
      if (!orgId || !userId) return;
      const {
        balance: balanceRes,
        subscriptionPeriodEnd: subscriptionPeriodEndRes,
      } = await getBalanceAndDate(orgId, userId);
      // Save the data we retreived from the db
      setBalance(balanceRes);
      setSubscriptionPeriodEnd(subscriptionPeriodEndRes);
    }

    // Call the API to grab the balance for an org
    else if (isBalanceForOrg(props)) {
      const { orgId } = props;
      if (!orgId) return;
      const {
        balance: balanceRes,
        subscriptionPeriodEnd: subscriptionPeriodEndRes,
      } = await getBalanceAndDate(orgId);
      setBalance(balanceRes);
      setSubscriptionPeriodEnd(subscriptionPeriodEndRes);
    }

    // Call the API to grab the balance for a user utilizing keyholder billing
    else if (featureEnabled('keyholderBillingFeature')) {
      const { billingCustomerNumber } = props;
      if (!billingCustomerNumber) {
        setCustomerNumber('--');
        return;
      }
      setCustomerNumber(billingCustomerNumber);
      const apiResponse = await getOverdueBalance({
        billingCustomerNumber,
      });
      // A null response signifies the user has no outstanding balance
      if (apiResponse === null) {
        setBalance(null);
        setSubscriptionPeriodEnd(null);
      } else {
        const { outstandingBalance, paymentDueDate } = apiResponse;
        setBalance(outstandingBalance);
        setSubscriptionPeriodEnd(paymentDueDate);
      }
    }
  }, [isBalanceForLegacyBillingUser, isBalanceForOrg, props]);

  const handleClickPayNow = useCallback(() => {
    document.dispatchEvent(new Event(SHOW_UPDATE_CARD_EVENT(auth.id)));
  }, [auth.id]);

  // Trigger grabbing the data as soon as the page loads
  useEffect(() => {
    const update = () => {
      fetchData();
    };

    fetchData();

    document.addEventListener(SUBSCRIPTION_UPDATED_EVENT, update);
    return () => {
      document.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, update);
    };
  }, [fetchData]); // Any time the fetchData method changes, call this useEffect again.

  // Calculate the display balance
  const displayBalance = useMemo(() => {
    // For overdue keyholder billing users with deactivated status, use the total from context
    if (
      isBalanceOverdue &&
      auth.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.deactivated &&
      loadingOverdueFees === 'done' &&
      proratedPricing !== undefined &&
      reinstatementFee !== undefined
    ) {
      return proratedPricing + reinstatementFee;
    }

    // Otherwise use the balance from API
    return balance;
  }, [
    isBalanceOverdue,
    auth.supraSubscription?.status,
    proratedPricing,
    reinstatementFee,
    loadingOverdueFees,
    balance,
  ]);

  let formattedBalance;
  if (subscriptionPeriodEnd || displayBalance) {
    let balanceToFormat = displayBalance;
    if (isBalanceForLegacyBillingUser(props) || isBalanceForOrg(props)) {
      balanceToFormat /= 100;
    }

    formattedBalance = formatCurrency(Number(balanceToFormat), {
      minimumFractionDigits: 2,
    });
  } else formattedBalance = '-';

  const dateObj = subscriptionPeriodEnd
    ? new Date(subscriptionPeriodEnd)
    : null;
  const formattedDate = dateObj ? format(dateObj, 'MMM d, yyyy') : '-';

  // The actual UI we will return
  return (
    <Card
      title="Balance"
      extra={
        customerNumber && (
          <Text style={StyledHeaderText}>Customer No. {customerNumber}</Text>
        )
      }
    >
      <StyledBody>
        <StyledSection>
          <StyledBalanceText
            isBalanceOverdue={isBalanceOverdue}
            title="Outstanding Balance"
            value={formattedBalance}
          />
          {isBalanceOverdue && <StyledLabel>Payment Required</StyledLabel>}
          {isBalanceOverdue && (
            <StyledButton
              dataCy={'pay-now-button'}
              size="small"
              onClick={handleClickPayNow}
            >
              Pay Now
            </StyledButton>
          )}
        </StyledSection>
        <StyledSection>
          <Statistic title="Next Invoice" value={formattedDate} />
        </StyledSection>
      </StyledBody>
    </Card>
  );
};

export default BalanceCard;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
`;
const StyledSection = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  padding: 16px;
  align-items: center;
`;

const StyledLabel = styled.div`
  display: flex;
  padding: 4px;
  color: ${Color.DODORIA};
  border: 1px solid ${Color.DODORIA};
  border-radius: 4px;
  background: ${Color.DODORIA_10};
`;

const StyledButton = styled(Button)`
  &&& {
    width: 98px;
    min-width: 98px;
  }

  margin-top: 8px;
  margin-bottom: 8px;
  margin-left: auto;
`;

const StyledBalanceText = styled(Statistic)<{ isBalanceOverdue?: boolean }>`
  margin-right: 24px;

  .ant-statistic-content {
    ${props => {
      if (props.isBalanceOverdue) {
        return css`
          color: ${Color.DODORIA};
        `;
      }
      return css`
        color: ${Color.PRIMARY_TEXT};
      `;
    }}
  }
`;

// Using a style like this since the text is nested inside a card and using the method above
// didn't seem to make the given styling a priority.
const StyledHeaderText = {
  color: Color.PRIMARY_TEXT,
  fontSize: 16,
  fontWeight: 400,
};
